/**
 * SuppliersPurchaseOrderList Component
 * 
 * This component manages the listing and actions for Work Orders (WO) 
 * created by subcontractor vendors. It provides a table interface with 
 * options to view, edit, delete, download, and invoice Work Orders.
 * 
 * Core Functionalities:
 * - Fetch and display Work Orders filtered by project and vendor type
 * - Support for project filtering using a modal
 * - Create, edit, and delete Work Orders based on user permissions
 * - Download Work Order documents in PDF/DOCX format
 * - Handle invoice creation and view past invoices
 * 
 * Features:
 * - Integrated with Redux for async data fetching (projects, WOs, templates)
 * - Uses `CustomTable` for displaying WO list with action popovers
 * - Uses `DeleteDialog`, `DownloadDialog`, and `PayInvoice` modals
 * - Applies data normalization using lodash and idx
 * - Provides user feedback via snackbar and dialog components
 *
 * Notes:
 * - Requires user to have write permissions (`userAccessControl === 2`) to access actions
 * - Routes are dynamically built using constants from `route-paths.js`
 */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import "./scss/po.scss";
import {
  VENDOR_PAYMENT,
  CREATE_WORK_ORDER,
  EDIT_WORK_ORDER,
  CONTRACTORS,
  WORK_ORDER,
} from "../../../constants/route-paths";
import {
  cancelWorkOrder,
  getPOList,
  deletePurchaseOrder,
  getProjectList,
  downloadProjectPO,
} from "../../../actions/project-management.action";
import idx from "idx";
import { format } from "date-fns";
import POFilter from "./wo-list-filter";
import PayInvoice from "../../vendor-payment/pay-invoice";
import _ from "lodash";
import DownloadDialog from "../../../components/download-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import { errorSnackBar } from "../../../actions/common.action";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { getGlobalSettingTemplateTypes } from "../../../actions/global-setting-template.action";
import { withRouter } from "../../../util/with-router";
import Popover from 'react-bootstrap/Popover';
import CustomTable from '../../../components/table/table';
import { woListColumns } from "../data/wo";


function SuppliersPurchaseOrderList({ projectModule, ...props }) {

  const [state, setState] = useState({
    poList: [],
    woNo: '',
    projectsList: [{ key: "All Projects", value: "all" }],
    selectedProject: "all",
    vendorType: "all",
    workType: "project",
    templateOptions: [],
    showPOFilter: false,
    downloadData: {
      open: false,
      id: "",
    },
    openDeleteDialog: false,
  })

  const writePermission = props.userAccessControl === 2 ? true : false;//Requires user to have write permissions (`userAccessControl === 2`) to access actions

  useEffect(() => {
    getPOList();
    getProjectList();
  }, [])

  const getProjectList = async () => {
    let projectsList = [];
    let projects = await props.getProjectList(null, "PROJECT");
    projects.forEach((project) => projectsList.push({ key: project.name, value: project.id }));

    projectsList = _.orderBy(
      projectsList,
      [({ key }) => key.toLowerCase()],
      ["asc"]
    );

    projectsList.unshift({ key: "All Projects", value: "all" });
    setState(prev => ({
      ...prev,
      projectsList
    }))
  };

  const getPOList = async (projectId = "", isDePo = null) => {
    let { list: poList } = await props.getPOList({ projectId, vendorType: 'sub-contractor', isDePo });

    const POs = [];
    let fields = ['street_address', 'suburb', 'state', 'postcode', 'country']
    Array.isArray(poList) && poList.forEach((po) => {
      const vendorName = idx(po, (_) => _.vendor.business_name) || "";
      const type = idx(po, (_) => _.vendor.type) || "";
      let po_address = [];
      for (const iterator of fields) {
        if (idx(po, _ => _[iterator])) po_address.push(idx(po, _ => _[iterator]))
      }
      const status = idx(po, (_) => _.status) || "";
      const project = idx(po, (_) => _.project.name) || "";
      const issueDate = po.createdAt ? format(new Date(po.createdAt), "dd/MM/yyyy") : "";
      const projectId = idx(po, (_) => _.project.id) || "";
      const projectType = idx(po, (_) => _.project.project_type) || "";
      const woTotal = parseFloat(po.woTotal || 0);
      const amountClaimed = parseFloat(po.amountClaimed || 0);
      const retention = parseFloat(po.retention || 0);
      const approved = parseFloat(po.approved || 0);

      POs.push({
        woTotal,
        amountClaimed,
        retention,
        approved,
        vendorName,
        type,
        address: po_address.join(', ') || idx(po, (_) => _.vendor.address.replace(/ /g, ', ')),
        project,
        projectId,
        projectType,
        issueDate,
        id: po.id,
        f_id: po.f_id,
        poId: po.u_id,
        status,
      });
    });
    setState(prev => ({
      ...prev,
      poList: POs
    }))
  };

  const createPo = () => {
    props.history(`/${CONTRACTORS}/${CREATE_WORK_ORDER}`);
  };

  const viewPO = () => {
    const { workType, poId } = state;
    props.history(`/${CONTRACTORS}/${WORK_ORDER}/view/${poId}/${workType}`);
  };

  const editWO = () => {
    const { workType, poId, projectName, projectId, projectType } = state;
    const path = `/${CONTRACTORS}/${EDIT_WORK_ORDER}`
      .replace(':poId', poId)
      .replace(':workType', workType);
    props.history(path, {
      state: {
        projectName,
        projectId,
        projectType
      }
    });
  };


  const deleteDialogState = () => {
    setState(prev => ({
      ...prev,
      openDeleteDialog: !prev.openDeleteDialog,
    }))
  };

  const deleteHandler = async () => {
    const { poId } = state;
    await props.deletePurchaseOrder(poId, "Work Order Deleted successfully");
    deleteDialogState();
    getPOList();
  };

  const poFilterHandler = () => {
    setState(prev => ({
      ...prev,
      showPOFilter: !prev.showPOFilter,
    }))
  };

  const changeFilter = (projectId, isDePo = null) => {
    if (projectId === "all") { projectId = ""; }
    poFilterHandler();
    getPOList(projectId, isDePo);
  };

  const handleMenu = (rowData) => {
    setState(prev => ({
      ...prev,
      poId: rowData.poId,
      woNo: rowData.id,
      projectName: rowData.project,
      projectId: rowData.projectId,
      projectType: rowData.projectType,
      invoiceData: {
        poNo: rowData.id,
        isSubContractor: rowData.type === "sub-contractor",
        status: rowData.status,
      },
    }))
  };

  const handlePayInvoiceModal = (status, allProjects = false) => {
    setState(prev => ({
      ...prev,
      showPayInvoice: status,
      invoiceData: { ...(allProjects ? {} : { ...prev.invoiceData }) },
    }))
  };

  const viewInvoices = () => {
    const { poId, workType } = state;
    props.history(`/${CONTRACTORS}/${VENDOR_PAYMENT}?poId=${poId}&type=${workType}`);
  };

  const gettemplate = async (type) => {
    let templateOptions = state;
    let newdata = [];
    let projectId = props.params.projectId;
    const templateData = await props.getGlobalSettingTemplateTypes(type, projectId);
    for (let key in templateData) {
      newdata.push({
        value: templateData[key].id,
        key: templateData[key].name,
      });
    }
    templateOptions = newdata;
    setState(prev => ({
      ...prev,
      templateOptions
    }))
  };

  const handleDownloadDialog = (id = "", templateType = "work_order", overrideData = null) => {
    const { downloadData, poId, templateOptions = [] } = state;
    const currentId = id && (typeof id === 'string' || typeof id === 'number') ? id : poId;
    const isOpening = !downloadData.open;

    const newDownloadData = {
      open: isOpening,
      id: currentId || "",
      template: "",
      overrideData: overrideData
    };

    if (isOpening) {
      gettemplate(templateType);
    }

    setState(prev => ({
      ...prev,
      downloadData: newDownloadData
    }))
  };

  const cancelWOHandler = async (id) => {
    if (window.confirm("Are you sure you want to cancel this Work Order?")) {
      const response = await props.cancelWorkOrder(id);
      if (response && response.status === "SUCCESS") {
        getPOList();
      }
    }
  };

  const downloadFile = async (fileFormat = "pdf", tempId) => {
    try {
      const { downloadData } = state;
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await props.downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)
          .catch(() => props.errorSnackBar('Failed to download PDF Work Order'));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await props.downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)
          .catch(() => props.errorSnackBar('Failed to download DOCX Work Order'));
      }
      handleDownloadDialog();
    } catch (error) {
      props.errorSnackBar('An error occurred while downloading the Work Order');
    }
  };

  return (
    <>
      <DeleteDialog
        open={state.openDeleteDialog}
        id={state.poId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName="Work Order"
        description={`Work Order - ${state.woNo}`}
        dataCy="delete-wo-dialog"
      />
      <DownloadDialog
        {...(state.downloadData || {})}
        downloadFile={downloadFile}
        templateOptions={state.templateOptions}
        handleDownloadDialog={handleDownloadDialog}
        downloadModuleName="Purchase Order"
        dataCy="download-wo-dialog"
      />
      {state.showPayInvoice && (
        <PayInvoice
          {...(state.invoiceData || {})}
          poId={state.poId}
          workType={"project"}
          handleDownloadDialog={handleDownloadDialog}
          handleClose={() => handlePayInvoiceModal(false)}
          getWOList={getPOList}
          dataCy="receive-invoice-wo-modal"
        />
      )}
      {state.showPOFilter && <POFilter
        projectsList={state.projectsList}
        changeFilter={changeFilter}
        workType={state.workType}
        open={state.showPOFilter}
        dialogClose={poFilterHandler}
        dataCy="wo-list-filter"
      />}

      {projectModule === false ? (
        <p data-cy="no-access-message">
          You do not currently have access to any Projects or Services. Please
          contact your Administrator if this is required.
        </p>
      ) : (
        <>
          <div className="purchase-order-block" data-cy="wo-list">
            <div className="row">
              <div className="w-auto">
                <h2 className="content-heading text-uppercase" data-cy="wo-list-heading">Work Order</h2>
              </div>
              {writePermission && (<div className="w-auto ms-auto mt-auto">
                <button onClick={createPo} className="secondarybtn secondarybtn-outline btn-ch ms-auto me-2 btn btn-primary" data-cy="create-wo">Create WO</button>
                <span onClick={poFilterHandler} className="filter-icon" data-cy="filter-icon"><FilterAltIcon /></span>
              </div>)}
            </div>

            <div className="row">
              <div className="col-12 mt-3" data-cy="wo-list-table-container">
                <CustomTable
                  columns={woListColumns}
                  data={state.poList}
                  isAction={true}
                  handleMenu={(data) => handleMenu(data)}
                  dataCy="wo-list-table"
                  menu={(data) => (
                    <Popover id="popover-basic" data-cy="wo-list-table-menu">
                      <Popover.Body>
                        <ul className="action-block-list">
                          {data && data.type === "sub-contractor" && (
                            <>
                              <li onClick={() => viewPO()} data-cy="view-wo">View WO</li>
                              {data && (data.type === "sub-contractor" ? (
                                <li onClick={() => handleDownloadDialog(data.poId, "work_order")} data-cy="download-wo">
                                  Download WO
                                </li>
                              ) : "")}
                              {writePermission && data && data.status !== "Completed" && data.status !== "Cancelled" && (<li onClick={() => editWO()} data-cy="edit-wo">Edit WO</li>)}
                              {writePermission && data && (data.status === "Pending" || data.status === "Active") && (<li onClick={() => cancelWOHandler(data.poId)} data-cy="cancel-wo" className="text-danger">Cancel</li>)}
                              {writePermission && data && data.status === "Pending" && (<li onClick={() => deleteDialogState()} data-cy="delete-wo">Delete</li>)}
                            </>
                          )}
                          {writePermission && data && data.status !== "Cancelled" && <li onClick={() => handlePayInvoiceModal(true)} data-cy="receive-invoice">Receive Invoice</li>}
                          {writePermission && <li onClick={() => viewInvoices()} data-cy="view-invoices">View Invoices</li>}
                        </ul>
                      </Popover.Body>
                    </Popover>
                  )}
                  fileName='WO List'
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  getPOList: bindActionCreators(getPOList, dispatch),
  cancelWorkOrder: bindActionCreators(cancelWorkOrder, dispatch),
  getGlobalSettingTemplateTypes: bindActionCreators(getGlobalSettingTemplateTypes, dispatch),
  getProjectList: bindActionCreators(getProjectList, dispatch),
  deletePurchaseOrder: bindActionCreators(deletePurchaseOrder, dispatch),
  downloadProjectPO: bindActionCreators(downloadProjectPO, dispatch),
  errorSnackBar: bindActionCreators(errorSnackBar, dispatch),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SuppliersPurchaseOrderList)
);

